import React, {useContext, useEffect, useState} from "react";
import Modal from "react-bootstrap/Modal";
import {Button} from "react-bootstrap";
import {FaArrowUp, FaCoins} from "react-icons/fa";
import {Context} from "../../../index";
import {purchaseBookAlwaysOnTop} from "../../../http/bookAPI";
import {check} from "../../../http/userAPI";

const BOOK_ALWAYS_ON_TOP_PRICES = {
    1: 3,
    2: 5,
    3: 7,
};

const BookAlwaysOnTop = ({
    show,
    handleClose,
    bookData,
    assignments = [],
    onPurchased,
}) => {
    const {user} = useContext(Context);
    const [days, setDays] = useState(1);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [untilOverride, setUntilOverride] = useState(null);

    useEffect(() => {
        if (!show) return;
        setDays(1);
        setFeedback(null);
        setUntilOverride(null);
    }, [show, bookData?.id]);

    const nowMs = Date.now();
    const alwaysOnTopUntil = untilOverride ?? bookData?.always_on_top_until;
    const isBookOnTopActive = Number(alwaysOnTopUntil || 0) > nowMs;
    const itemLabel = bookData?.book_type === "product" ? "product" : "book";
    const itemTitleLabel = bookData?.book_type === "product" ? "Product" : "Book";
    const isOutsideTariffPlan = bookData?.assignment_creation_locked === true;
    const activeAssignments = assignments.filter((assignment) =>
        assignment?.assignment_status === "NEW" &&
        assignment?.reviewer_reading_step === "NO_STEP"
    );
    const hasActiveAssignmentOnTop = activeAssignments.some((assignment) =>
        Number(assignment?.always_on_top_until || 0) > nowMs
    );
    const hasNoActiveAssignments = activeAssignments.length === 0;
    const isPurchaseDisabled = loading || hasActiveAssignmentOnTop || isOutsideTariffPlan;

    const formatDateTimeFromMs = (ms) => {
        const value = Number(ms);
        if (!Number.isFinite(value) || value <= 0) return "Not active";
        try {
            return new Intl.DateTimeFormat("en-GB", {
                dateStyle: "medium",
                timeStyle: "short",
            }).format(new Date(value));
        } catch {
            return new Date(value).toLocaleString();
        }
    };

    const handlePurchase = async () => {
        setLoading(true);
        setFeedback(null);
        try {
            const result = await purchaseBookAlwaysOnTop(bookData?.id, days);
            if (result?.book?.always_on_top_until) {
                setUntilOverride(result.book.always_on_top_until);
            }

            const freshUser = await check();
            user.setUser(freshUser);
            onPurchased?.(result);

            if (result?.warning) {
                setFeedback({
                    variant: "warning",
                    message: `${itemTitleLabel} Always on Top was purchased successfully. Please note: this ${itemLabel} has no active assignments right now, so it will appear in the library/store only after you create an open assignment.`,
                });
            } else {
                setFeedback({
                    variant: "success",
                    message: result?.message || `${itemTitleLabel} Always on Top activated`,
                });
            }
        } catch (error) {
            const message = error.response?.data?.message || `Failed to activate ${itemTitleLabel} Always on Top`;
            setFeedback({
                variant: "danger",
                message,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} top>
            <div className="modal-content">
                <div className="modal-header small" style={{height: 40}}>
                    <h6 className="modal-title fs-6 mb-0">
                        <FaArrowUp className="me-2 text-success" />
                        {itemTitleLabel} Always on Top
                    </h6>
                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={handleClose}
                        disabled={loading}
                    />
                </div>

                <div className="modal-body small">
                    <p className="mb-2 text-muted" style={{lineHeight: "1.35"}}>
                        {itemTitleLabel} Always on Top until:{" "}
                        <strong>{formatDateTimeFromMs(alwaysOnTopUntil)}</strong>
                    </p>

                    {isOutsideTariffPlan && (
                        <div className="alert alert-danger py-2 px-3 mb-2" style={{lineHeight: "1.35"}}>
                            This {itemLabel} is outside your plan. Always on Top purchase is not available.
                        </div>
                    )}

                    {hasNoActiveAssignments && (
                        <div className="alert alert-warning py-2 px-3 mb-2" style={{lineHeight: "1.35"}}>
                            This {itemLabel} has no active assignments. Always on Top time starts now, but the {itemLabel} will appear only after you create an open assignment.
                        </div>
                    )}

                    {hasActiveAssignmentOnTop && (
                        <div className="alert alert-danger py-2 px-3 mb-2" style={{lineHeight: "1.35"}}>
                            This {itemLabel} has an active assignment Always on Top. {itemTitleLabel} Always on Top cannot be purchased until it expires.
                        </div>
                    )}

                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <select
                            className="form-select form-select-sm"
                            style={{width: 170}}
                            value={days}
                            onChange={(event) => setDays(Number(event.target.value))}
                            disabled={isPurchaseDisabled}
                        >
                            <option value={1}>1 day - $3</option>
                            <option value={2}>2 days - $5</option>
                            <option value={3}>3 days - $7</option>
                        </select>

                        <span className="text-muted">
                            Price: <strong>${BOOK_ALWAYS_ON_TOP_PRICES[days].toFixed(2)}</strong>
                        </span>
                    </div>

                    {feedback && (
                        <div className={`alert alert-${feedback.variant} py-2 px-3 mt-2 mb-0`} style={{lineHeight: "1.35"}}>
                            {feedback.message}
                        </div>
                    )}

                </div>

                <div
                    className="modal-footer small d-flex align-items-center justify-content-end gap-2 px-2"
                    style={{height: 50, paddingTop: 0, paddingBottom: 0}}
                >
                    <Button
                        variant="success"
                        size="sm"
                        className="shadow-sm"
                        onClick={handlePurchase}
                        disabled={isPurchaseDisabled}
                    >
                        {loading ? "Processing..." : (
                            <>
                                <FaCoins className="me-2" />
                                {isBookOnTopActive ? "Extend" : "Purchase"}
                            </>
                        )}
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default BookAlwaysOnTop;
